import React, { Component, Fragment } from 'react';
import moment from 'moment-timezone';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { noop } from 'lodash';
import { Trans, withI18n } from 'lingui-react';
import { compose, setDisplayName } from 'recompose';

import { Container, Row, Column } from 'styled-components-reactive-grid';
import { px2rem, buildStyledComponent } from 'decisiv-ui-utils';

import {
  ButtonLink,
  DateRangeSelector,
  Dropdown,
  InputField,
  InputGroup,
} from 'base-components';

function formattedDateRange({ from: start, to: end } = {}) {
  return [
    start && moment(start).format('D MMM YYYY'),
    end && moment(end).format('D MMM YYYY'),
  ]
    .filter(Boolean)
    .join(' - ');
}

const DropdownContainer = buildStyledComponent(
  'DropdownContainer',
  styled(InputGroup.Column),
  `
    width: 100%;
    max-width: ${px2rem(370)};
    margin-bottom: -1px;

    @media (max-width: ${px2rem(1024)}) {
      max-width: none;
    }
  `,
);

class DateRangeSelectorDropdown extends Component {
  static propTypes = {
    i18n: PropTypes.shape({ t: PropTypes.func.isRequired }).isRequired,
    endDate: PropTypes.instanceOf(Date),
    onChange: PropTypes.func,
    startDate: PropTypes.instanceOf(Date),
  };

  static defaultProps = {
    endDate: undefined,
    onChange: noop,
    startDate: undefined,
  };

  state = { startDate: this.props.startDate, endDate: this.props.endDate };

  reset = () =>
    this.setState({
      startDate: this.props.startDate,
      endDate: this.props.endDate,
    });

  clearAndApply = (...args) => {
    this.setState({ startDate: undefined, endDate: undefined }, () => {
      this.handleApply(...args);
    });
  };

  handleDateChange = ({ from, to }) =>
    this.setState({ startDate: from, endDate: to });

  handleClear = () =>
    this.setState({ startDate: undefined, endDate: undefined });

  handleApply = ({ hide }) => {
    this.props.onChange(this.state);

    hide();
  };

  render() {
    const { i18n } = this.props;
    const { startDate, endDate } = this.state;
    const selectedDateRange = { from: startDate, to: endDate };
    const hasAnyDate = !!startDate || !!endDate;

    return (
      <DropdownContainer>
        <Dropdown
          onChange={noop}
          position="bottomLeft"
          onExpandedChange={this.reset}
        >
          {({ show, hide, toggle, isVisible }) => (
            <Fragment>
              <Container modifiers="padScale_0" onClick={show}>
                <InputField
                  name="dateRange"
                  value={formattedDateRange(selectedDateRange)}
                  onChange={noop}
                  placeholder={i18n.t`Select date range...`}
                >
                  <InputField.Icon name="calendar" />
                  <Column modifiers={['col', 'padScale_0']}>
                    <Row>
                      <InputField.Label>
                        <Trans>Date</Trans>
                      </InputField.Label>
                    </Row>
                    <Row>
                      <InputField.TextField
                        readOnly
                        autoComplete="off"
                        style={{ cursor: 'pointer', caretColor: 'transparent' }}
                      />
                      {hasAnyDate && (
                        <InputField.ActionButton
                          icon="times"
                          type="button"
                          onClick={event => {
                            event.stopPropagation();
                            this.clearAndApply({ hide });
                          }}
                          modifiers={['hoverDanger']}
                        />
                      )}
                      <InputField.ActionButton
                        icon={isVisible ? 'chevron-up' : 'chevron-down'}
                        type="button"
                        onClick={event => {
                          event.stopPropagation();
                          toggle();
                        }}
                        modifiers={['hoverInfo']}
                      />
                    </Row>
                  </Column>
                </InputField>
              </Container>
              <Dropdown.Content style={{ width: px2rem(370), zIndex: 2 }}>
                <DateRangeSelector
                  onSelectDate={this.handleDateChange}
                  disabledDates={{ after: new Date() }}
                  selectedDateRange={selectedDateRange}
                />
                <Dropdown.SectionBody
                  style={{ paddingTop: 0, marginTop: px2rem(-5) }}
                >
                  <Column modifiers={['col', 'start', 'padScale_0']}>
                    <ButtonLink
                      type="button"
                      onClick={this.handleClear}
                      disabled={!hasAnyDate}
                      modifiers={!hasAnyDate && 'disabled'}
                    >
                      <Trans>Clear</Trans>
                    </ButtonLink>
                  </Column>
                  <Column modifiers={['end', 'padScale_0']}>
                    <ButtonLink
                      type="button"
                      onClick={() => this.handleApply({ hide })}
                    >
                      <Trans>Apply</Trans>
                    </ButtonLink>
                  </Column>
                </Dropdown.SectionBody>
              </Dropdown.Content>
            </Fragment>
          )}
        </Dropdown>
      </DropdownContainer>
    );
  }
}

export default compose(
  setDisplayName('DateRangeSelectorDropdown'),
  withI18n(),
)(DateRangeSelectorDropdown);



// WEBPACK FOOTER //
// ./src/compositions/CasesSearch/Filters/DateRangeSelectorDropdown.js